import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, PhoneCall, FileText, Hammer, RefreshCw,
  Globe, KeyRound, Tag, Users, MapPin, Bot,
} from "lucide-react";
import { Reveal, SectionLabel, Stagger, Item } from "../components/Reveal";
import OpsDashboard from "../components/OpsDashboard";
import FAQ from "../components/FAQ";
import SEO from "../components/SEO";
import { WhatsAppButton, AuditButton, FinalCta } from "../components/CTA";
import { ChecklistVisual, PhoneVisual, DashboardVisual } from "../components/ServiceVisuals";
import { useContent, chipFor, visibleWork, faqsFor, waLink } from "../data/siteContent";

const services = [
  {
    key: "brand", name: "Brand & Website", icon: Globe, visual: DashboardVisual,
    desc: "Logo, brand kit and a fast mobile website that brings enquiries, not just looks good.",
    to: "/services/brand-website", testid: "service-card-brand",
  },
  {
    key: "whatsapp", name: "WhatsApp & AI Automation", icon: null, visual: PhoneVisual,
    desc: "Auto-replies, booking links, payment and follow-up reminders. Your business answers even at 2 AM.",
    to: "/services/automation", testid: "service-card-whatsapp",
  },
  {
    key: "sops", name: "SOPs & Operations", icon: null, visual: ChecklistVisual,
    desc: "Written SOPs, daily reports and a trained operations executive, so work doesn't depend on you.",
    to: "/services/sops", testid: "service-card-sops",
  },
];

const launchIncludes = [
  "5-page mobile website",
  "Google Business Profile set up",
  "WhatsApp chat button and auto-reply",
  "Enquiry form straight to your Google Sheet",
  "Basic local SEO",
  "Domain in your name",
];

const steps = [
  { num: "01", icon: PhoneCall, label: "Free 15-minute call", sub: "We check your Google listing, website and WhatsApp." },
  { num: "02", icon: FileText, label: "Fixed quote", sub: "Scope, price and timeline in writing." },
  { num: "03", icon: Hammer, label: "Build", sub: "10 working days. You approve each step." },
  { num: "04", icon: RefreshCw, label: "Run", sub: "Monthly care, reports and improvements." },
];

const why = [
  { icon: KeyRound, title: "You own everything.", desc: "Domain, code, logo and accounts in your name, with a written copyright transfer." },
  { icon: Tag, title: "Published prices.", desc: "No hidden renewals, no surprise bills." },
  { icon: Users, title: "One team to build and run.", desc: "Design, automation and operations under one roof." },
  { icon: MapPin, title: "Local and reachable.", desc: "Noida and Ghaziabad. A real person on WhatsApp." },
  { icon: Bot, title: "AI-fast, human-checked.", desc: "We use AI tools to work faster; every project is reviewed by a person before it goes live." },
];

export default function Home() {
  const content = useContent();
  const chips = { brand: chipFor(content, "brand"), whatsapp: chipFor(content, "whatsapp"), sops: chipFor(content, "sops") };
  const work = visibleWork(content, true);
  const faqs = faqsFor(content, "home");

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "ALLCOV LLP",
    url: "https://www.allcov.com/",
    telephone: content.contact.phone_display,
    email: content.contact.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: content.contact.address,
      addressLocality: "Noida",
      addressRegion: "Uttar Pradesh",
      postalCode: "201301",
      addressCountry: "IN",
    },
    areaServed: ["Noida", "Ghaziabad"],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Services",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Brand & Website" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "WhatsApp & AI Automation" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "SOPs & Operations" } },
      ],
    },
  };
  const faqSchema = faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <div data-testid="home-page">
      <SEO
        title="Allcov — Website, WhatsApp & Business Automation | Noida & Ghaziabad"
        description="Logo, website and WhatsApp automation for clinics, coaching centres and local businesses. Live in 10 working days. Launch Kit from ₹14,999."
        path="/"
        jsonLd={faqSchema ? [orgSchema, faqSchema] : [orgSchema]}
      />

      {/* HERO — static, first paint */}
      <section className="relative overflow-hidden pb-20 pt-36 lg:pb-24 lg:pt-44" data-testid="hero-section">
        <div className="absolute -bottom-32 -left-40 -z-10 h-[520px] w-[520px] rounded-full bg-myrtle/20 blur-[140px]" aria-hidden="true" />
        <div className="visual-grid absolute inset-0 -z-10 opacity-40" aria-hidden="true" />
        <div className="mx-auto grid w-full max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
          <div>
            <p className="mb-7 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-pop" data-testid="hero-eyebrow">
              <span className="inline-block h-px w-10 bg-myrtle" aria-hidden="true" />
              Brand · Website · Automation · SOPs — Noida & Ghaziabad
            </p>
            <h1 className="tracking-tight" data-testid="hero-headline">
              <span className="block font-glacial text-[38px] font-extrabold uppercase leading-[1.05] text-txt sm:text-6xl lg:text-[72px]">
                We Build It.
              </span>
              <span className="block font-batangas text-5xl leading-[1.05] text-pop sm:text-7xl lg:text-[80px]">
                Then We Run It.
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-base text-mut md:text-lg" data-testid="hero-sub">
              Your logo, website and WhatsApp automation — live in 10 working days. Then we keep it running every month, so you can focus on your customers.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <WhatsAppButton testid="hero-whatsapp" />
              <AuditButton testid="hero-audit" />
            </div>
            <p className="mt-5 text-xs uppercase tracking-[0.2em] text-faint" data-testid="hero-ownership">
              You own everything — domain, website, logo and accounts in your name.
            </p>
          </div>
          <OpsDashboard />
        </div>
      </section>

      {/* THREE SERVICES */}
      <section id="services" className="border-t border-line py-24 lg:py-32" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><SectionLabel>What We Do</SectionLabel></Reveal>
          <h2 className="mb-14 font-batangas text-4xl tracking-tight md:text-[50px]">Three services. One team.</h2>
          <Stagger className="grid gap-6 lg:grid-cols-3">
            {services.map((s) => (
              <Item key={s.key} className="h-full">
                <div data-testid={s.testid} className="group flex h-full flex-col border border-line bg-surface transition-transform duration-300 hover:-translate-y-1.5">
                  <div className="relative aspect-[16/10] overflow-hidden border-b border-line">
                    <s.visual />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-batangas text-2xl tracking-tight">{s.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-mut">{s.desc}</p>
                    <div className="mt-auto flex flex-col items-start gap-4 pt-7">
                      {chips[s.key] && (
                        <span className="whitespace-nowrap bg-myrtle/10 px-3 py-2 font-batangas text-[11px] tracking-tight text-pop min-[400px]:text-xs sm:px-4 sm:text-sm" data-testid={`${s.testid}-price`}>
                          {chips[s.key]}
                        </span>
                      )}
                      <Link
                        to={s.to}
                        data-testid={`${s.testid}-link`}
                        className="group/link inline-flex items-center gap-1.5 whitespace-nowrap text-xs uppercase tracking-[0.2em] text-mut transition-colors duration-300 hover:text-pop"
                      >
                        Learn More
                        <ArrowUpRight size={14} className="transition-transform duration-300 group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* LAUNCH KIT */}
      <section className="section-light py-24 lg:py-32" data-testid="launch-kit-section">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-10">
          <div>
            <Reveal><SectionLabel>Launch Kit</SectionLabel></Reveal>
            <h2 className="font-batangas text-4xl leading-tight tracking-tight md:text-[50px]">
              For clinics, coaching centres and local businesses.
            </h2>
            <Reveal delay={0.1}>
              <div className="mt-8 flex flex-wrap items-end gap-4">
                <p className="font-batangas text-6xl tracking-tight text-pop" data-testid="launch-kit-price">₹14,999</p>
                <div className="pb-2">
                  <p className="text-sm text-mut">one-time · + GST</p>
                  <p className="text-xs text-faint">Regular ₹19,999 — founding price for our first 5 clients</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 inline-block border border-line px-4 py-2 text-xs uppercase tracking-[0.2em] text-mut" data-testid="launch-kit-timeline">
                Live in 10 working days
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-sm text-mut">
                Care plan: ₹1,999/month — hosting, backups, updates, 1 hour of changes and a monthly enquiry report.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <Link
                to="/contact"
                data-testid="launch-kit-cta"
                className="group mt-9 inline-flex items-center gap-2 bg-myrtle px-9 py-4 text-sm uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-myrtle-light"
              >
                Book Your Launch Kit
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="inner-glow border border-line bg-surface p-7 md:p-9">
              <p className="text-xs uppercase tracking-[0.25em] text-faint">Everything included</p>
              <ul className="mt-6 space-y-4">
                {launchIncludes.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-mut">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-myrtle" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="process" className="py-24 lg:py-32" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><SectionLabel>How It Works</SectionLabel></Reveal>
          <h2 className="mb-16 font-batangas text-4xl tracking-tight md:text-[50px]">Simple, start to finish.</h2>
          <div className="relative">
            <div className="absolute left-0 right-0 top-16 hidden lg:block" aria-hidden="true">
              <svg className="h-2 w-full" preserveAspectRatio="none">
                <line x1="0" y1="4" x2="100%" y2="4" stroke="var(--pop)" strokeWidth="1" strokeDasharray="6 10" className="animate-dash" opacity="0.35" />
              </svg>
            </div>
            <Stagger className="relative grid gap-12 md:grid-cols-2 md:gap-8 lg:grid-cols-4">
              {steps.map((s, i) => (
                <Item key={s.num}>
                  <div className="relative" data-testid={`process-step-${i + 1}`}>
                    <span className="block font-batangas text-[90px] leading-none text-faint md:text-[100px]">{s.num}</span>
                    <div className="mt-4 flex h-14 w-14 items-center justify-center border border-line bg-surface lg:-mt-8">
                      <s.icon size={24} className="text-pop" strokeWidth={1.5} />
                    </div>
                    <p className="mt-5 font-batangas text-xl tracking-tight md:text-2xl">{s.label}</p>
                    <p className="mt-2 text-sm text-mut">{s.sub}</p>
                  </div>
                </Item>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* OUR WORK */}
      <section className="section-light py-24 lg:py-32" data-testid="work-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="mb-14 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <Reveal><SectionLabel>Our Work</SectionLabel></Reveal>
              <h2 className="font-batangas text-4xl tracking-tight md:text-[50px]">Built, not borrowed.</h2>
            </div>
            <Reveal delay={0.1}>
              <Link to="/work" data-testid="work-view-all" className="group inline-flex shrink-0 items-center gap-2 text-sm uppercase tracking-[0.15em] text-mut transition-colors duration-300 hover:text-pop">
                See All Work <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {work.map((w) => (
              <Item key={w.id || w.name} className="h-full">
                <div className="group flex h-full flex-col border border-line bg-surface p-7 transition-transform duration-300 hover:-translate-y-1.5" data-testid={`work-card-${w.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {w.image ? (
                    <img src={w.image} alt={w.name} className="h-8 w-auto object-contain" loading="lazy" />
                  ) : (
                    <Globe size={26} className="text-pop" strokeWidth={1.5} />
                  )}
                  <p className="mt-6 font-batangas text-xl tracking-tight">{w.name}</p>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-mut">{w.summary}</p>
                  <p className="mt-5 text-[10px] uppercase tracking-[0.2em] text-faint">{w.label}</p>
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* QUIKPIK */}
      <section className="relative overflow-hidden py-24 lg:py-32" data-testid="quikpik-section">
        <div className="absolute -right-40 top-0 -z-10 h-[420px] w-[420px] rounded-full bg-myrtle/15 blur-[140px]" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><SectionLabel>Quikpik by Allcov</SectionLabel></Reveal>
          <h2 className="max-w-3xl font-batangas text-4xl leading-tight tracking-tight md:text-[50px]">
            Skip the line.
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base text-mut md:text-lg">
              Customers pre-order coffee and snacks and pick up at the counter — no queue, no delivery commission.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 text-xs uppercase tracking-[0.25em] text-faint">
              {content.quikpik.area}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-wrap items-center gap-5">
              <WhatsAppButton label="Partner with Quikpik" testid="quikpik-partner" text={content.quikpik.wa_message} />
              <Link to="/quikpik" data-testid="quikpik-learn-more" className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-mut transition-colors duration-300 hover:text-pop">
                Learn More <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY ALLCOV */}
      <section id="why" className="section-light py-24 lg:py-32" data-testid="why-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal><SectionLabel>Why Allcov</SectionLabel></Reveal>
          <h2 className="mb-14 font-batangas text-4xl tracking-tight md:text-[50px]">Built different, on purpose.</h2>
          <Stagger className="grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-5">
            {why.map((w) => (
              <Item key={w.title} className="h-full">
                <div className="flex h-full flex-col gap-5 bg-surface p-6" data-testid={`why-card-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <w.icon size={24} className="text-pop" strokeWidth={1.5} />
                  <div>
                    <p className="font-batangas text-lg tracking-tight">{w.title}</p>
                    <p className="mt-2 text-xs leading-relaxed text-mut">{w.desc}</p>
                  </div>
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="py-24 lg:py-32" data-testid="founder-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-4">
            <div className="mx-auto flex h-56 w-56 items-center justify-center rounded-full border border-line bg-surface lg:h-72 lg:w-72" data-testid="founder-photo">
              <span className="font-batangas text-6xl text-pop">KA</span>
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal><SectionLabel>Founder</SectionLabel></Reveal>
            <h2 className="font-batangas text-4xl leading-tight tracking-tight md:text-[44px]">
              Hi, I'm Kaushlesh Agarwal.
            </h2>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-mut md:text-lg">
                I started Allcov to give small businesses the systems big companies have — without the big-company price. I review every project myself.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <a
                href={waLink(content, "Hi Kaushlesh, I would like a free audit")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-whatsapp"
                className="group mt-9 inline-flex items-center gap-2 bg-myrtle px-9 py-4 text-sm uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-myrtle-light"
              >
                WhatsApp Kaushlesh
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="section-light py-24 lg:py-32" data-testid="faq-section">
          <div className="mx-auto max-w-4xl px-6 lg:px-10">
            <Reveal><SectionLabel>FAQ</SectionLabel></Reveal>
            <h2 className="mb-12 font-batangas text-4xl tracking-tight md:text-[50px]">Straight answers.</h2>
            <Reveal delay={0.1}>
              <FAQ items={faqs} testid="home-faq" />
            </Reveal>
          </div>
        </section>
      )}

      <FinalCta />
    </div>
  );
}
